import { useState, useEffect } from 'react';
import { 
  ShoppingCart, Package, Truck, CheckCircle, XCircle, Clock,
  Phone, MapPin, User, ChevronDown, ChevronUp, RefreshCw, Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

// Order Status Badge
const OrderStatusBadge = ({ status, language }) => {
  const statusConfig = {
    pending: {
      bg: 'bg-amber-100 text-amber-700',
      bgDark: 'bg-amber-500/20 text-amber-400',
      icon: Clock,
      label: language === 'ar' ? 'في الانتظار' : 'Pending'
    },
    confirmed: {
      bg: 'bg-blue-100 text-blue-700',
      bgDark: 'bg-blue-500/20 text-blue-400',
      icon: Package,
      label: language === 'ar' ? 'مؤكد' : 'Confirmed'
    },
    processing: {
      bg: 'bg-purple-100 text-purple-700',
      bgDark: 'bg-purple-500/20 text-purple-400',
      icon: Package,
      label: language === 'ar' ? 'قيد التجهيز' : 'Processing'
    },
    shipped: {
      bg: 'bg-indigo-100 text-indigo-700',
      bgDark: 'bg-indigo-500/20 text-indigo-400',
      icon: Truck,
      label: language === 'ar' ? 'تم الشحن' : 'Shipped'
    },
    delivered: {
      bg: 'bg-green-100 text-green-700',
      bgDark: 'bg-green-500/20 text-green-400',
      icon: CheckCircle,
      label: language === 'ar' ? 'تم التوصيل' : 'Delivered'
    },
    cancelled: {
      bg: 'bg-red-100 text-red-700',
      bgDark: 'bg-red-500/20 text-red-400',
      icon: XCircle,
      label: language === 'ar' ? 'ملغي' : 'Cancelled'
    }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg}`}>
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
};

// Single Order Card
const OrderCard = ({ order, language, isDarkMode, onStatusUpdate, updating }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return `${price?.toLocaleString() || 0} ${language === 'ar' ? 'درهم' : 'AED'}`;
  };

  const statusActions = {
    pending: ['confirmed', 'cancelled'],
    confirmed: ['processing', 'cancelled'],
    processing: ['shipped', 'cancelled'],
    shipped: ['delivered'],
    delivered: [],
    cancelled: []
  };

  const nextStatuses = statusActions[order.status] || [];

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'
    }`}>
      {/* Order Header */}
      <div 
        className={`p-4 cursor-pointer hover:bg-opacity-50 transition-colors ${
          isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/10'}`}>
              <ShoppingCart className="h-5 w-5 text-[#1E9BD9]" />
            </div>
            <div>
              <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                #{order.order_id}
              </p>
              {order.receipt_number && (
                <p
                  className={`font-mono text-[11px] inline-flex items-center gap-1 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}
                  data-testid={`order-receipt-${order.order_id}`}
                >
                  <Receipt className="h-3 w-3" />
                  {language === 'ar' ? 'إيصال' : 'Receipt'}: {order.receipt_number}
                </p>
              )}
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatDate(order.created_at)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {formatPrice(order.store_total || order.total)}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {order.items?.length || 0} {language === 'ar' ? 'منتج' : 'item(s)'}
              </p>
            </div>
            <OrderStatusBadge status={order.status} language={language} />
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className={`border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          {/* Customer Info */}
          <div className={`p-4 ${isDarkMode ? 'bg-[#0F172A]/50' : 'bg-gray-50'}`}>
            <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ar' ? 'معلومات العميل' : 'Customer Info'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                  {order.delivery_info?.full_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className={isDarkMode ? 'text-white' : 'text-gray-800'} dir="ltr">
                  {order.delivery_info?.phone}
                </span>
              </div>
              <div className="flex items-start gap-2 md:col-span-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                <span className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                  {order.delivery_info?.emirate}, {order.delivery_info?.area}
                  {order.delivery_info?.address && ` - ${order.delivery_info.address}`}
                </span>
              </div>
              {order.delivery_info?.notes && (
                <div className="md:col-span-2">
                  <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    "{order.delivery_info.notes}"
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="p-4">
            <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ar' ? 'المنتجات' : 'Products'}
            </h4>
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {item.name || item.name_en}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatPrice(item.price)} × {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          {nextStatuses.length > 0 && (
            <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {language === 'ar' ? 'تحديث الحالة' : 'Update Status'}
              </h4>
              <div className="flex flex-wrap gap-2">
                {nextStatuses.map(status => (
                  <Button
                    key={status}
                    size="sm"
                    disabled={updating}
                    onClick={() => onStatusUpdate(order.order_id, status)}
                    className={
                      status === 'cancelled' 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : status === 'delivered'
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-[#1E9BD9] hover:bg-[#1E9BD9]/90'
                    }
                  >
                    {status === 'confirmed' && (language === 'ar' ? 'تأكيد الطلب' : 'Confirm')}
                    {status === 'processing' && (language === 'ar' ? 'قيد التجهيز' : 'Processing')}
                    {status === 'shipped' && (language === 'ar' ? 'تم الشحن' : 'Shipped')}
                    {status === 'delivered' && (language === 'ar' ? 'تم التوصيل' : 'Delivered')}
                    {status === 'cancelled' && (language === 'ar' ? 'إلغاء' : 'Cancel')}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Orders Tab Component
export const OrdersTab = ({ storeId, language, isDarkMode, BACKEND_URL }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState(''); // iter-298 — search by order_id OR receipt_number
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });

  const fetchOrders = async (page = 1, status = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('business_token');
      let url = `${BACKEND_URL}/api/merchant/stores/${storeId}/orders?page=${page}&limit=10`;
      if (status && status !== 'all') {
        url += `&status=${status}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrders(response.data.orders || []);
      setPagination(response.data.pagination || { page: 1, total: 0, pages: 1 });
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب الطلبات' : 'Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchOrders(1, filter === 'all' ? null : filter);
    }
  }, [storeId, filter]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('business_token');
      await axios.put(
        `${BACKEND_URL}/api/merchant/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'ar' ? 'تم تحديث حالة الطلب' : 'Order status updated');
      fetchOrders(pagination.page, filter === 'all' ? null : filter);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(language === 'ar' ? 'خطأ في تحديث الطلب' : 'Error updating order');
    } finally {
      setUpdating(false);
    }
  };

  const filters = [
    { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
    { id: 'pending', label: language === 'ar' ? 'في الانتظار' : 'Pending' },
    { id: 'confirmed', label: language === 'ar' ? 'مؤكد' : 'Confirmed' },
    { id: 'processing', label: language === 'ar' ? 'قيد التجهيز' : 'Processing' },
    { id: 'shipped', label: language === 'ar' ? 'تم الشحن' : 'Shipped' },
    { id: 'delivered', label: language === 'ar' ? 'تم التوصيل' : 'Delivered' },
    { id: 'cancelled', label: language === 'ar' ? 'ملغي' : 'Cancelled' }
  ];

  return (
    <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
          {language === 'ar' ? 'الطلبات' : 'Orders'}
          {pagination.total > 0 && (
            <span className={`text-sm font-normal ms-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              ({pagination.total})
            </span>
          )}
        </h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => fetchOrders(pagination.page, filter === 'all' ? null : filter)}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* iter-301 — AI order analysis panel */}
      <AiAnalysisPanel
        storeId={storeId}
        language={language}
        isDarkMode={isDarkMode}
        BACKEND_URL={BACKEND_URL}
      />

      {/* iter-298 — Search bar (order_id OR receipt_number) */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={language === 'ar' ? 'ابحث برقم الطلب أو رقم الإيصال...' : 'Search by order ID or receipt number...'}
          data-testid="orders-search-input"
          className={`w-full px-4 py-2 rounded-lg text-sm border transition-colors ${
            isDarkMode
              ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#38BDF8]'
              : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#1E9BD9]'
          } focus:outline-none`}
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.id
                ? isDarkMode 
                  ? 'bg-[#38BDF8] text-white' 
                  : 'bg-[#1E9BD9] text-white'
                : isDarkMode
                  ? 'bg-white/5 text-gray-400 hover:bg-white/10'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className={`h-8 w-8 mx-auto mb-4 animate-spin ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingCart className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
          <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
            {language === 'ar' ? 'لا توجد طلبات' : 'No orders found'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {(() => {
            // iter-298 — client-side search filter on the current page.
            const q = search.trim().toLowerCase();
            const filtered = q
              ? orders.filter((o) => {
                  const oid = String(o.order_id || '').toLowerCase();
                  const rno = String(o.receipt_number || '').toLowerCase();
                  return oid.includes(q) || rno.includes(q);
                })
              : orders;
            if (filtered.length === 0) {
              return (
                <div className="text-center py-8" data-testid="orders-search-empty">
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {language === 'ar' ? 'لا توجد نتائج تطابق البحث.' : 'No orders match your search.'}
                  </p>
                </div>
              );
            }
            return filtered.map(order => (
              <OrderCard
                key={order.id || order.order_id}
                order={order}
                language={language}
                isDarkMode={isDarkMode}
                onStatusUpdate={handleStatusUpdate}
                updating={updating}
              />
            ));
          })()}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => fetchOrders(page, filter === 'all' ? null : filter)}
              className={`w-10 h-10 rounded-lg font-medium ${
                page === pagination.page
                  ? 'bg-[#1E9BD9] text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-400 hover:bg-white/20'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// iter-301 — AI order analysis panel (unified bot ASB-AI-GEM-CGB-001)
const AiAnalysisPanel = ({ storeId, language, isDarkMode, BACKEND_URL }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const runAnalysis = async () => {
    try {
      setBusy(true);
      setAnalysis(null);
      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/ai/product/analyze-orders`,
        { store_id: storeId, window_days: windowDays },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      setAnalysis(data);
      setOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'failed';
      toast.error((language === 'ar' ? 'فشل التحليل: ' : 'Analysis failed: ') + String(detail).slice(0, 120));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`mb-4 rounded-xl border p-3 ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border-emerald-400/20' : 'bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200'}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">🧠</span>
          <div>
            <p className={`text-sm font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
              {language === 'ar' ? 'تحليل الطلبات بالذكاء الاصطناعي' : 'AI order analysis'}
            </p>
            <p className={`text-[10px] ${isDarkMode ? 'text-emerald-400/70' : 'text-emerald-700/80'}`}>
              {language === 'ar' ? 'ملخص فوري + نصائح عملية بناءً على بياناتك.' : 'Instant summary + actionable tips from your data.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(parseInt(e.target.value))}
            disabled={busy}
            className={`text-xs rounded-md px-2 py-1 border ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
            data-testid="ai-analysis-window"
          >
            <option value={7}>{language === 'ar' ? 'آخر 7 أيام' : 'Last 7 days'}</option>
            <option value={30}>{language === 'ar' ? 'آخر 30 يوماً' : 'Last 30 days'}</option>
            <option value={90}>{language === 'ar' ? 'آخر 90 يوماً' : 'Last 90 days'}</option>
          </select>
          <button
            onClick={runAnalysis}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition ${busy ? 'opacity-60 cursor-wait' : ''} bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 shadow-sm`}
            data-testid="ai-analysis-run-btn"
          >
            {busy ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                {language === 'ar' ? 'جارٍ التحليل…' : 'Analyzing…'}
              </>
            ) : (
              <>✨ {language === 'ar' ? 'حلّل الآن' : 'Analyze now'}</>
            )}
          </button>
        </div>
      </div>

      {open && analysis && (
        <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-emerald-200'} space-y-2 text-xs`} data-testid="ai-analysis-result">
          {analysis.summary && (
            <p className={`${isDarkMode ? 'text-white' : 'text-gray-800'} leading-relaxed`}>
              <strong>{language === 'ar' ? 'الملخص: ' : 'Summary: '}</strong>{analysis.summary}
            </p>
          )}
          {analysis.highlights?.length > 0 && (
            <div>
              <p className={`font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                {language === 'ar' ? '📌 أبرز النقاط' : '📌 Highlights'}
              </p>
              <ul className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'} list-disc list-inside leading-relaxed`}>
                {analysis.highlights.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </div>
          )}
          {analysis.recommendations?.length > 0 && (
            <div>
              <p className={`font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                {language === 'ar' ? '💡 توصيات' : '💡 Recommendations'}
              </p>
              <ul className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'} list-disc list-inside leading-relaxed`}>
                {analysis.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          <div className={`text-[10px] italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {language === 'ar'
              ? `بناءً على ${analysis.stats?.total_orders ?? 0} طلب خلال ${analysis.stats?.window_days ?? windowDays} يوماً.`
              : `Based on ${analysis.stats?.total_orders ?? 0} orders over ${analysis.stats?.window_days ?? windowDays} days.`}
          </div>
        </div>
      )}
    </div>
  );
};



export default OrdersTab;
